import { z } from 'zod'
import { BOARD_STATES } from '@/features/hr-attendance-board/api'
import { companyTodayIsoDate } from '@/lib/company-date'

/**
 * The attendance screen's URL search params.
 *
 * Filters live in the URL so a view is linkable and survives a reload
 * (`apps/company/AGENTS.md`), and Zod validates them at the boundary with the
 * inferred type flowing inward — nothing here restates a shape `z.infer`
 * already gives.
 *
 * `from` and `to` are optional: absent means today, resolved when the screen
 * renders, so a tab left open across midnight follows the clock.
 */
const schema = z.object({
  /** Day: one row per employee per day. Month: one employee's month. */
  view: z.enum(['day', 'month']).default('day'),
  /** Y-m-d. The Day view's window; equal dates are one day. */
  from: z.string().optional(),
  to: z.string().optional(),
  /** Y-m, for the monthly sheet. */
  month: z.string().default(() => thisMonth()),
  department_id: z.string().optional(),
  employee_id: z.string().optional(),
  schedule_id: z.string().optional(),
  search: z.string().optional(),
  // The board owns this list. Importing it rather than repeating it is why
  // adding a ninth state cannot leave this screen filtering on eight.
  state: z.enum(BOARD_STATES).optional(),
  sort: z.enum(['name', 'department', 'state', 'lateness']).default('name'),
  direction: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(200).default(25),
  /** The employee whose day the drawer shows, and that day. */
  open: z.string().optional(),
  open_date: z.string().optional(),
})

export const attendanceSearchSchema = z.preprocess(legacySearch, schema)

export type AttendanceSearch = z.infer<typeof schema>

/**
 * The request queues that left this screen (D8), by their old `tab` value. The
 * route sends such a link to the attendance requests screen.
 */
export function movedRequestsTab(
  search: Record<string, unknown> | undefined,
): 'freelance-hours' | 'remote-work' | null {
  const tab = search?.tab
  return tab === 'freelance-hours' || tab === 'remote-work' ? tab : null
}

/**
 * Old links keep working (D10): `tab=monthly` is the Month view, the Daily and
 * Department tabs are the Day view, and the board's `date` is its window.
 */
function legacySearch(input: unknown): unknown {
  if (typeof input !== 'object' || input === null) return input
  const { date, tab, ...rest } = input as Record<string, unknown>
  const next: Record<string, unknown> = { ...rest }

  if (next.view === undefined && tab === 'monthly') next.view = 'month'
  if (typeof date === 'string' && next.from === undefined) {
    next.from = date
    next.to = date
  }

  return next
}

/**
 * The current month, computed at parse time rather than at module load: a tab
 * left open across midnight on the 1st should not keep defaulting to last
 * month.
 */
function thisMonth(): string {
  return companyTodayIsoDate().slice(0, 7)
}
